import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { HubSpotService } from '../services/hubspot';
import { SalesforceService } from '../services/salesforce';
import { Json } from '@supabase/supabase-js';
import { create } from 'zustand';

// Import the config interfaces from the service files
interface SalesforceConfig {
  clientId: string;
  clientSecret: string;
  instanceUrl: string;
}

interface HubSpotConfig {
  apiKey: string;
  portalId: string;
}

interface CRMConfig {
  id?: string;
  chatbot_public_id: string;
  crm_type: 'salesforce' | 'hubspot';
  client_id: string;
  client_secret: string;
  access_token?: string;
  refresh_token?: string;
  instance_url?: string;
  created_at?: string;
  updated_at?: string;
  is_enabled?: boolean;
}

interface Database {
  public: {
    Tables: {
      chatbot_crm_configs: {
        Row: CRMConfig;
        Insert: Omit<CRMConfig, 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Omit<CRMConfig, 'id' | 'created_at' | 'updated_at'>>;
      };
    };
  };
}

interface ChatbotCRMIntegrationStore {
  config: CRMConfig | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  salesforceService: SalesforceService | null;
  hubspotService: HubSpotService | null;
  clearError: () => void;
  initializeServices: (chatbotPublicId: string) => Promise<void>;
  updateSalesforceConfig: (config: Partial<CRMConfig>, chatbotPublicId: string) => Promise<void>;
  updateHubspotConfig: (config: Partial<CRMConfig>, chatbotPublicId: string) => Promise<void>;
  syncLead: (leadId: string) => Promise<void>;
}

const DEFAULT_CONFIG: CRMConfig = {
  chatbot_public_id: '',
  crm_type: 'salesforce',
  client_id: '',
  client_secret: '',
  is_enabled: false
};

export const useChatbotCRMIntegration = create<ChatbotCRMIntegrationStore>((set, get) => ({
  config: null,
  loading: false,
  error: null,
  initialized: false,
  salesforceService: null,
  hubspotService: null,

  clearError: () => set({ error: null }),

  initializeServices: async (chatbotPublicId: string) => {
    try {
      set({ loading: true, error: null });

      const { data: configData, error: configError } = await supabase
        .from('chatbot_crm_configs')
        .select('*')
        .eq('chatbot_public_id', chatbotPublicId)
        .single();

      // Si l'erreur est PGRST116 (not found), ce n'est pas une erreur critique
      // car cela signifie simplement qu'aucune configuration n'existe encore
      if (configError && configError.code !== 'PGRST116') {
        console.warn('Non-critical CRM config error:', configError);
      }

      const config = configData || { ...DEFAULT_CONFIG, chatbot_public_id: chatbotPublicId };

      // Initialiser les services seulement si la configuration est activée
        let salesforceService = null;
        let hubspotService = null;

      if (config.crm_type === 'salesforce' && config.is_enabled) {
        try {
          salesforceService = new SalesforceService(config);
        } catch (e) {
          console.error('Error initializing Salesforce service:', e);
          if (config.is_enabled) {
            // Afficher l'erreur seulement si le service est censé être activé
            set({ 
              error: 'Salesforce service initialization failed. Please check your configuration.' 
            });
          }
        }
      } else if (config.crm_type === 'hubspot' && config.is_enabled) {
        try {
          hubspotService = new HubSpotService(config);
        } catch (e) {
          console.error('Error initializing HubSpot service:', e);
          if (config.is_enabled) {
            // Afficher l'erreur seulement si le service est censé être activé
            set({ 
              error: 'HubSpot service initialization failed. Please check your configuration.' 
            });
          }
        }
        }

        set({
        config,
          salesforceService,
          hubspotService,
          initialized: true
        });
    } catch (error) {
      console.error('Error in initializeServices:', error);
      // N'afficher l'erreur que si c'est une erreur critique (pas liée à l'absence de configuration)
      if (error instanceof Error && !error.message.includes('not found')) {
        set({ error: `Failed to initialize CRM services: ${error.message}` });
      }
    } finally {
      set({ loading: false });
    }
  },

  updateSalesforceConfig: async (config: Partial<CRMConfig>, chatbotPublicId: string) => {
    try {
      set({ loading: true, error: null });

      // Vérifier les champs obligatoires si l'intégration est activée
      if (config.is_enabled) {
        const missingFields = [];
        if (!config.client_id) missingFields.push('Client ID');
        if (!config.client_secret) missingFields.push('Client Secret');
        if (!config.instance_url) missingFields.push('Instance URL');
        
        if (missingFields.length > 0) {
          throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
        }
      }

      const { data: existingConfig, error: fetchError } = await supabase
        .from('chatbot_crm_configs')
        .select('*')
        .eq('chatbot_public_id', chatbotPublicId)
        .maybeSingle();

      if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;

      const updatedConfig: CRMConfig = {
        ...(existingConfig || DEFAULT_CONFIG),
        ...config,
        crm_type: 'salesforce',
        chatbot_public_id: chatbotPublicId
      };

      const { error } = await supabase
        .from('chatbot_crm_configs')
        .upsert(updatedConfig);

      if (error) throw error;

      // Créer le service seulement si l'intégration est activée
      const salesforceService = updatedConfig.is_enabled ? new SalesforceService(updatedConfig) : null;

      set({
        config: updatedConfig,
        salesforceService,
        // Si on désactive, on supprime les erreurs précédentes
        error: updatedConfig.is_enabled ? get().error : null
      });
    } catch (error) {
      console.error('Error updating Salesforce config:', error);
      set({ 
        error: error instanceof Error 
          ? `Failed to update Salesforce config: ${error.message}` 
          : 'Failed to update Salesforce config'
      });
    } finally {
      set({ loading: false });
    }
  },

  updateHubspotConfig: async (config: Partial<CRMConfig>, chatbotPublicId: string) => {
    try {
      set({ loading: true, error: null });

      // Vérifier les champs obligatoires si l'intégration est activée
      if (config.is_enabled) {
        const missingFields = [];
        if (!config.client_id) missingFields.push('API Key');
        if (!config.client_secret) missingFields.push('Portal ID');
        
        if (missingFields.length > 0) {
          throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
        }
      }

      const { data: existingConfig, error: fetchError } = await supabase
        .from('chatbot_crm_configs')
        .select('*')
        .eq('chatbot_public_id', chatbotPublicId)
        .maybeSingle();

      if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;

      const updatedConfig: CRMConfig = {
        ...(existingConfig || DEFAULT_CONFIG),
        ...config,
        crm_type: 'hubspot',
        chatbot_public_id: chatbotPublicId
      };

      const { error } = await supabase
        .from('chatbot_crm_configs')
        .upsert(updatedConfig);

      if (error) throw error;

      // Créer le service seulement si l'intégration est activée
      const hubspotService = updatedConfig.is_enabled ? new HubSpotService(updatedConfig) : null;

      set({
        config: updatedConfig,
        hubspotService,
        // Si on désactive, on supprime les erreurs précédentes
        error: updatedConfig.is_enabled ? get().error : null
      });
    } catch (error) {
      console.error('Error updating HubSpot config:', error);
      set({ 
        error: error instanceof Error 
          ? `Failed to update HubSpot config: ${error.message}` 
          : 'Failed to update HubSpot config'
      });
    } finally {
      set({ loading: false });
    }
  },

  syncLead: async (leadId: string) => {
    const { config, salesforceService, hubspotService } = get();
    if (!config) {
      set({ error: 'No CRM configuration found. Please configure a CRM integration first.' });
      return;
    }

    if (!config.is_enabled) {
      set({ error: 'CRM integration is disabled. Please enable it first.' });
      return;
    }

    try {
      set({ loading: true, error: null });

      if (config.crm_type === 'salesforce') {
        if (!salesforceService) {
          throw new Error('Salesforce service not initialized. Please check your configuration.');
        }
        await salesforceService.syncLead(leadId);
      } else if (config.crm_type === 'hubspot') {
        if (!hubspotService) {
          throw new Error('HubSpot service not initialized. Please check your configuration.');
        }
        await hubspotService.syncLead(leadId);
      } else {
        throw new Error(`Unknown CRM type: ${config.crm_type}`);
      }
    } catch (error) {
      console.error('Error syncing lead:', error);
      set({ 
        error: error instanceof Error 
          ? `Failed to sync lead: ${error.message}` 
          : 'Failed to sync lead' 
      });
    } finally {
      set({ loading: false });
    }
  }
}));